import React from 'react';
import { Link } from 'react-router-dom';

const s = {
  page: { paddingTop: '64px', background: 'var(--cream)', minHeight: '100vh' },
  hero: { background: 'var(--navy)', color: 'white', padding: '64px 0 56px' },
  heroInner: { maxWidth: '800px', margin: '0 auto', padding: '0 48px' },
  heroEyebrow: {
    fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.18em',
    textTransform: 'uppercase', color: 'var(--amber-light)',
    marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px',
  },
  eyebrowLine: { display: 'block', width: '24px', height: '1px', background: 'var(--amber-light)' },
  heroTitle: {
    fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 4vw, 2.8rem)',
    fontWeight: 600, lineHeight: 1.1, marginBottom: '16px',
  },
  heroSub: {
    fontSize: '0.95rem', fontWeight: 300, opacity: 0.75, lineHeight: 1.7, maxWidth: '560px',
  },

  main: { maxWidth: '800px', margin: '0 auto', padding: '48px 48px 80px' },

  backLink: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    color: 'var(--navy)', fontSize: '0.85rem', fontWeight: 500,
    textDecoration: 'none', marginBottom: '40px',
  },

  section: { marginBottom: '48px' },
  sectionRule: {
    border: 'none', borderTop: '1px solid var(--border)',
    margin: '48px 0',
  },
  sectionLabel: {
    fontSize: '0.68rem', fontWeight: 500, letterSpacing: '0.16em',
    textTransform: 'uppercase', color: 'var(--amber)',
    marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px',
  },
  sectionLabelLine: { display: 'block', width: '20px', height: '1px', background: 'var(--amber)' },
  sectionTitle: {
    fontFamily: 'var(--font-display)', fontSize: '1.4rem',
    fontWeight: 600, color: 'var(--navy)', marginBottom: '20px',
  },
  p: {
    fontSize: '0.93rem', color: '#374151', lineHeight: 1.85,
    fontWeight: 300, marginBottom: '20px',
  },
  pStrong: { fontWeight: 600, color: 'var(--navy)' },
  pEm: { fontStyle: 'italic' },

  studyBox: {
    background: '#F0F4F8', borderRadius: '10px',
    borderLeft: '4px solid var(--navy)',
    padding: '20px 24px', marginBottom: '20px',
  },
  studyTitle: {
    fontSize: '0.85rem', fontWeight: 600, color: 'var(--navy)', marginBottom: '8px',
  },
  studyBody: {
    fontSize: '0.85rem', color: '#374151', lineHeight: 1.75, fontWeight: 300,
  },
  studySource: {
    fontSize: '0.75rem', color: 'var(--muted)', fontStyle: 'italic', marginTop: '8px',
  },

  tierRow: {
    display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'flex-start',
  },
  tierBadge: (color) => ({
    flexShrink: 0, fontSize: '0.7rem', fontWeight: 700, padding: '4px 12px',
    borderRadius: '999px', background: color === 'navy' ? '#EFF6FF' : color === 'amber' ? '#FFFBEB' : '#F9FAFB',
    color: color === 'navy' ? 'var(--navy)' : color === 'amber' ? 'var(--amber)' : 'var(--muted)',
    border: `1px solid ${color === 'navy' ? '#DBEAFE' : color === 'amber' ? '#FDE68A' : 'var(--border)'}`,
    marginTop: '2px',
  }),
  tierText: { fontSize: '0.88rem', color: '#374151', lineHeight: 1.7, fontWeight: 300 },

  signoff: {
    marginTop: '48px', padding: '32px',
    background: 'var(--navy)', borderRadius: '12px',
    color: 'white',
  },
  signoffText: {
    fontSize: '0.93rem', lineHeight: 1.85, fontWeight: 300,
    opacity: 0.85, marginBottom: '20px',
  },
  signoffName: { fontSize: '1rem', fontWeight: 600, color: 'var(--amber-light)', marginBottom: '4px' },
  signoffTitle: { fontSize: '0.82rem', opacity: 0.6, fontWeight: 300 },

  compliance: {
    marginTop: '32px', padding: '24px', background: '#FFFBEB',
    borderRadius: '10px', fontSize: '0.76rem', color: '#92400E', lineHeight: 1.7,
  },
};

export default function HowItWasBuilt() {
  return (
    <div style={s.page}>
      <div style={s.hero}>
        <div style={s.heroInner}>
          <div style={s.heroEyebrow}>
            <span style={s.eyebrowLine} />
            How This Was Built
          </div>
          <h1 style={s.heroTitle}>The methodology, the science,<br />and the reasoning behind it</h1>
          <p style={s.heroSub}>
            A plain account of what this tool is, what it's based on, and what it isn't.
          </p>
        </div>
      </div>

      <div style={s.main}>
        <Link to="/" style={s.backLink}>← Back to home</Link>

        {/* Opening */}
        <div style={s.section}>
          <p style={s.p}>
            This tool was built by one person — me, Bob Ferguson. I'm an independent Shaklee distributor.
            My family has been in this business for 56 years. I built it because the gap between what the
            nutritional science actually says and what most people ever hear about it is larger than it should
            be — and because I had access to the scientific resources, the product knowledge, and the family
            history to do something about it.
          </p>
          <p style={s.p}>
            But the deeper reason is this: 56 years of watching people try to get healthy taught me that
            supplements alone rarely produce the transformation people are looking for. What actually works
            is addressing as much of the whole picture as possible — sleep, movement, stress, diet quality,
            circadian rhythm, mind-body practice, community, creative life — and then using targeted nutrition
            to fill the gaps that lifestyle alone can't close. Most tools address one variable. This one was
            built to look at all of them, meet you where you are, and identify the highest-leverage changes
            available to you specifically.
          </p>
        </div>

        <hr style={s.sectionRule} />

        {/* The Foundation */}
        <div style={s.section}>
          <div style={s.sectionLabel}><span style={s.sectionLabelLine} /> The Foundation</div>
          <h2 style={s.sectionTitle}>How the assessment works</h2>
          <p style={s.p}>
            The assessment asks 40 questions across 9 domains — demographics, dietary pattern, lifestyle
            factors, sleep, stress, energy, movement, current supplementation, and health priorities.
            No disease-state information is collected. The answers drive two outputs: a personalized lifestyle guidance report across 15 lifestyle domains and a personalized supplement protocol, both specific to what you described.
          </p>
          <p style={s.p}>
            The engine behind this tool is a structured review of independent clinical literature across
            37 healthy lifestyle domains — sleep science, circadian biology, stress physiology, movement,
            diet quality, mind-body practice, social connection, and targeted nutrition among them. That
            research base drives all three outputs: the lifestyle guidance, the dietary recommendations,
            and the supplement protocol. The supplement recommendations additionally draw on a graded review of 180 published studies — 140 drawn from independent clinical literature across 37 nutrition domains and 40 from Shaklee's own clinical research archive, classified by evidence grade — clinical studies, observational
            research, and expert opinion — and NHANES population data identifying the nutritional gaps The coach draws on the complete body of published nutrition and lifestyle science — the curated library represents verified anchors, not the ceiling of what it knows.
            most prevalent by age, sex, and dietary pattern. The product set covers 94 Shaklee products
            with complete label data. You can follow the evidence behind any recommendation directly through{' '}
            <Link to="/science" style={{ color: 'var(--navy)', fontWeight: 500 }}>The Science</Link>,
            linked from the home page.
          </p>

          <p style={s.p}>The assessment produces three types of output: a lifestyle guidance report addressing the behavioral and habit domains most relevant to your profile, dietary priorities specific to your inputs, and a personalized supplement protocol. The supplement protocol is organized into three tiers:</p>

          <div style={s.tierRow}>
            <span style={s.tierBadge('navy')}>Foundation</span>
            <div style={s.tierText}>Dietary and supplement recommendations that address the nutritional gaps most likely to matter for your age, sex, dietary pattern, and lifestyle — derived from NHANES population data. These apply broadly regardless of specific goals.</div>
          </div>
          <div style={s.tierRow}>
            <span style={s.tierBadge('amber')}>Targeted</span>
            <div style={s.tierText}>Modules activated by your specific assessment inputs, corresponding to health domains including cognitive function, sleep, stress response, cardiovascular support, hormonal transition, and athletic performance.</div>
          </div>
          <div style={s.tierRow}>
            <span style={s.tierBadge('muted')}>Optional</span>
            <div style={s.tierText}>Supplemental additions relevant to your profile, presented without the same weight as Foundation or Targeted recommendations.</div>
          </div>
        </div>

        <hr style={s.sectionRule} />

        {/* Research Worth Naming */}
        <div style={s.section}>
          <div style={s.sectionLabel}><span style={s.sectionLabelLine} /> The Research Worth Naming</div>
          <h2 style={s.sectionTitle}>Two bodies of independent research</h2>
          <p style={s.p}>
            Two bodies of independent research stand behind this tool that deserve specific mention.
          </p>

          <div style={s.studyBox}>
            <div style={s.studyTitle}>The Landmark Studies</div>
            <div style={s.studyBody}>
              Landmark I at UC Berkeley (Block et al.) and Landmark II at Tufts University (Jacques and Rogers)
              found that long-term Shaklee supplement users had significantly better cardiometabolic biomarker
              profiles than non-users or those on conventional single supplements. These were not Shaklee
              studies. They were independent research programs that Shaklee funded and entrusted to two of
              the most respected nutrition research institutions in the country.
            </div>
            <div style={s.studySource}>
              Block G et al. Nutr J. 2007;6:30. — Jacques PF, Rogers G. Int J Vitam Nutr Res. 2021.
            </div>
          </div>

          <div style={s.studyBox}>
            <div style={s.studyTitle}>The Telomere Research</div>
            <div style={s.studyBody}>
              Conducted in collaboration with Nobel laureate Elizabeth Blackburn at UCSF, this research
              found that long-term Shaklee users had measurably longer telomeres than age-matched non-users.
              Telomere length is a direct biological marker of cellular aging. This finding has not been
              replicated in the broader supplement category.
            </div>
            <div style={s.studySource}>
              Harley CB et al. 2014. Wang H et al. J Food Nutr Sci. 2018;6(5):129-134.
            </div>
          </div>
        </div>

        <hr style={s.sectionRule} />

        {/* What This Tool Is Not */}
        <div style={s.section}>
          <div style={s.sectionLabel}><span style={s.sectionLabelLine} /> Important Disclosures</div>
          <h2 style={s.sectionTitle}>What this tool is not</h2>
          <p style={s.p}>
            This tool does not diagnose, treat, cure, or prevent any disease. No medical guidance is offered or implied.
          </p>
          <p style={s.p}>
            This tool is not affiliated with Shaklee Corporation. I am an independent distributor.
            Shaklee Corporation did not build this tool, does not operate it, and has not authorized
            or endorsed it.
          </p>
          <p style={s.p}>
            If you are pregnant, nursing, taking medications, or managing a health condition, consult
            a qualified healthcare professional before beginning any supplement program.
          </p>
        </div>

        <hr style={s.sectionRule} />

        {/* Signoff */}
        <div style={s.signoff}>
          <p style={s.signoffText}>
            I built this tool because people deserve access to the best available nutritional science,
            presented honestly, without promotional distortion. The recommendations reflect what the
            evidence actually supports — not what sells. Every claim is sourced, every source available,
            and the complete knowledge base is accessible through{' '}
            <Link to="/science" style={{ color: 'var(--amber-light)', fontWeight: 500 }}>The Science</Link>,
            linked from the home page and every protocol page.
          </p>
          <div style={s.signoffName}>Bob Ferguson</div>
          <div style={s.signoffTitle}>Independent Shaklee Distributor · lifestyleintelligencehub.com</div>
        </div>

        <div style={s.compliance}>
          <strong>Important.</strong> These statements have not been evaluated by the Food and Drug Administration.
          Products listed in this protocol are not intended to diagnose, treat, cure, or prevent any disease.
          This tool is authored and operated by an independent Shaklee distributor and is not affiliated with
          or authorized by Shaklee Corporation.
        </div>
      </div>
    </div>
  );
}
